import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const AddClientModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    clientType: 'individual',
    name: '',
    email: '',
    phone: '',
    mpesaNumber: '',
    county: '',
    address: '',
    businessRegNumber: '',
    idNumber: '',
    taxPin: '',
    notes: '',
    portalAccess: false,
    communicationPreference: 'email'
  });

  const [errors, setErrors] = useState({});

  const kenyanCounties = [
    'Nairobi', 'Mombasa', 'Kisumu', 'Nakuru', 'Eldoret', 'Kiambu', 'Machakos', 'Meru',
    'Nyeri', 'Thika', 'Garissa', 'Kakamega', 'Malindi', 'Kitale', 'Embu', 'Kericho'
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+254\s?\d{9}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid Kenyan phone number (+254XXXXXXXXX)';
    }

    if (!formData.county) {
      newErrors.county = 'County is required';
    }

    if (formData.clientType === 'corporate' && !formData.businessRegNumber.trim()) {
      newErrors.businessRegNumber = 'Business registration number is required for corporate clients';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Here you would typically send the data to your backend
      console.log('New client data:', formData);
      
      // Reset form and close modal
      setFormData({
        clientType: 'individual',
        name: '',
        email: '',
        phone: '',
        mpesaNumber: '',
        county: '',
        address: '',
        businessRegNumber: '',
        idNumber: '',
        taxPin: '',
        notes: '',
        portalAccess: false,
        communicationPreference: 'email'
      });
      setErrors({});
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-surface rounded-lg shadow-dialog w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-heading font-semibold text-text-primary">Add New Client</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary-100 rounded-lg transition-smooth focus:outline-none focus:ring-2 focus:ring-secondary-400"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Client Type */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Client Type *
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="clientType"
                  value="individual"
                  checked={formData.clientType === 'individual'}
                  onChange={(e) => handleInputChange('clientType', e.target.value)}
                  className="mr-2 text-primary focus:ring-primary"
                />
                Individual
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="clientType"
                  value="corporate"
                  checked={formData.clientType === 'corporate'}
                  onChange={(e) => handleInputChange('clientType', e.target.value)}
                  className="mr-2 text-primary focus:ring-primary"
                />
                Corporate
              </label>
            </div>
          </div>

          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                {formData.clientType === 'corporate' ? 'Company Name' : 'Full Name'} *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                  errors.name ? 'border-error' : 'border-border'
                }`}
                placeholder={formData.clientType === 'corporate' ? 'Enter company name' : 'Enter full name'}
              />
              {errors.name && <p className="text-error text-sm mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Email Address *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                  errors.email ? 'border-error' : 'border-border'
                }`}
                placeholder="Enter email address"
              />
              {errors.email && <p className="text-error text-sm mt-1">{errors.email}</p>}
            </div>
          </div>

          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                  errors.phone ? 'border-error' : 'border-border'
                }`}
                placeholder="+254 712 345 678"
              />
              {errors.phone && <p className="text-error text-sm mt-1">{errors.phone}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                M-Pesa Number
              </label>
              <input
                type="tel"
                value={formData.mpesaNumber}
                onChange={(e) => handleInputChange('mpesaNumber', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="+254 712 345 678"
              />
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              County *
            </label>
            <select
              value={formData.county}
              onChange={(e) => handleInputChange('county', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                errors.county ? 'border-error' : 'border-border'
              }`}
            >
              <option value="">Select County</option>
              {kenyanCounties.map(county => (
                <option key={county} value={county}>{county}</option>
              ))}
            </select>
            {errors.county && <p className="text-error text-sm mt-1">{errors.county}</p>}
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Address
            </label>
            <textarea
              value={formData.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Enter full address including P.O. Box if applicable"
            />
          </div>

          {/* Corporate-specific fields */}
          {formData.clientType === 'corporate' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Business Registration Number *
                </label>
                <input
                  type="text"
                  value={formData.businessRegNumber}
                  onChange={(e) => handleInputChange('businessRegNumber', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                    errors.businessRegNumber ? 'border-error' : 'border-border'
                  }`}
                  placeholder="C.123456/2023"
                />
                {errors.businessRegNumber && <p className="text-error text-sm mt-1">{errors.businessRegNumber}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Tax PIN
                </label>
                <input
                  type="text"
                  value={formData.taxPin}
                  onChange={(e) => handleInputChange('taxPin', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="P123456789A"
                />
              </div>
            </div>
          )}

          {/* Individual-specific fields */}
          {formData.clientType === 'individual' && (
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                ID Number
              </label>
              <input
                type="text"
                value={formData.idNumber}
                onChange={(e) => handleInputChange('idNumber', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="12345678"
              />
            </div>
          )}

          {/* Communication Preferences */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Preferred Communication Method
            </label>
            <select
              value={formData.communicationPreference}
              onChange={(e) => handleInputChange('communicationPreference', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="email">Email</option>
              <option value="phone">Phone</option>
              <option value="sms">SMS</option>
              <option value="whatsapp">WhatsApp</option>
            </select>
          </div>

          {/* Portal Access */}
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.portalAccess}
                onChange={(e) => handleInputChange('portalAccess', e.target.checked)}
                className="mr-2 text-primary focus:ring-primary rounded"
              />
              <span className="text-sm text-text-primary">Enable client portal access</span>
            </label>
            <p className="text-xs text-text-secondary mt-1">
              Client will be able to view case updates and documents online
            </p>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Any additional notes about the client..."
            />
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-text-secondary hover:text-text-primary hover:bg-secondary-100 rounded-lg transition-smooth focus:outline-none focus:ring-2 focus:ring-secondary-400"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-700 transition-smooth focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              Add Client
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddClientModal;